import React, { useState } from 'react';
import { Check, ChevronDown, MessageSquare, Sparkles, User, Lock, Info } from 'lucide-react';

const SCALE_COLORS = [
  '#F47920', '#3B82F6', '#14B8A6', '#8B5CF6', '#22C55E', '#F59E0B', '#F43F5E', '#6366F1',
];

const ALL_SCALES = {
  '1': { name: 'Analytical Thinking', shortName: 'Analytical' },
  '2': { name: 'Communication & Clarity', shortName: 'Communication' },
  '3': { name: 'Adaptability & Learning', shortName: 'Adaptability' },
  '4': { name: 'Ownership & Results', shortName: 'Ownership' },
  '5': { name: 'Teamwork & Influence', shortName: 'Teamwork' },
  '6': { name: 'Motivation & Career', shortName: 'Motivation' },
  '7': { name: 'Self-Awareness & Maturity', shortName: 'Self-Awareness' },
  '8': { name: 'Domain Expertise', shortName: 'Domain Skills' },
};

function expandBarsItems(bars) {
  if (!bars || typeof bars !== 'object') return [];

  const items = [];
  const pushScale = (scaleId, payload = {}, primary = false) => {
    const numericScaleId = Number(scaleId);
    const score = Number(payload?.score);
    if (!Number.isFinite(numericScaleId) || numericScaleId < 1 || numericScaleId > 8) return;
    if (!Number.isFinite(score) || score <= 0) return;

    items.push({
      target_scale: numericScaleId,
      score,
      confidence: payload?.confidence ?? null,
      primary,
    });
  };

  pushScale(bars.target_scale, bars, true);

  const crossSignals = bars.cross_signals;
  if (crossSignals && typeof crossSignals === 'object') {
    Object.entries(crossSignals).forEach(([scaleId, payload]) => {
      pushScale(scaleId, payload, false);
    });
  }

  return items.sort((left, right) => {
    if (left.primary && !right.primary) return -1;
    if (!left.primary && right.primary) return 1;
    return Number(left.target_scale) - Number(right.target_scale);
  });
}

function ScoreBar({ label, score, maxScore = 5, color, locked = false }) {
  const pct = (score / maxScore) * 100;

  return (
    <div className="flex items-center gap-2.5 py-1.5 relative">
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-1">
          <span className={`text-[12px] font-medium truncate transition-all ${locked ? 'blur-[3px] text-gray-400' : 'text-gray-600'}`}>
            {label}
          </span>
          <span
            className={`text-[11px] font-bold ml-2 transition-all ${locked ? 'blur-[3px] text-gray-400' : ''}`}
            style={{ color: locked ? undefined : color }}
          >
            {score.toFixed(1)}/{maxScore}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ease-out ${locked ? 'blur-[3px] bg-gray-300' : ''}`}
            style={{ width: `${pct}%`, background: locked ? undefined : `linear-gradient(90deg, ${color}CC, ${color})` }}
          />
        </div>
      </div>
    </div>
  );
}

function QuestionScales({ bars, locked }) {
  const [expanded, setExpanded] = useState(false);
  const barsData = expandBarsItems(bars);
  const avg = barsData.reduce((sum, b) => sum + (b.score || 0), 0) / (barsData.length || 1);

  return (
    <div className="mt-3">
      <button
        onClick={(event) => {
          event.stopPropagation();
          setExpanded((prev) => !prev);
        }}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all w-full text-left active:scale-[0.98] ${
          locked ? 'bg-gray-50 border-gray-200' : 'bg-orange-50/50 border-orange-100'
        }`}
      >
        <span className="text-[12px] font-bold text-[#1E2A3A]">Scores</span>
        <span className={`flex-1 text-[12px] font-bold ${locked ? 'text-gray-500' : 'text-[#1E2A3A]'}`}>
          {locked
            ? 'Competency Scores'
            : barsData.length
              ? `Competency Scores - avg ${avg.toFixed(1)}/5`
              : 'Competency Scores - not available yet'}
        </span>
        {locked && <Lock className="w-3 h-3 text-gray-400" />}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      <div className={`overflow-hidden transition-all duration-300 ${expanded ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
        <div className="px-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            {barsData.map((item, index) => {
              const scale = ALL_SCALES[String(item.target_scale)] || { name: 'Scale' };
              return (
                <ScoreBar
                  key={`${item.target_scale || 'scale'}-${index}`}
                  label={item.primary ? `${scale.name} (primary)` : scale.name}
                  score={item.score || 0}
                  color={SCALE_COLORS[index % SCALE_COLORS.length]}
                  locked={locked}
                />
              );
            })}

            {!locked && barsData.length === 0 && (
              <div className="col-span-full py-2 px-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2 mt-1">
                <Info className="w-3.5 h-3.5 text-blue-700" />
                <span className="text-[11px] font-bold text-blue-800">
                  Score for this answer is temporarily unavailable. Your feedback is saved.
                </span>
              </div>
            )}

            {locked && (
              <div className="col-span-full py-2 px-3 bg-amber-50 border border-amber-100 rounded-lg flex items-center gap-2 mt-1">
                <span className="text-[11px] font-bold text-amber-800">Unlock detailed scoring with Pro plan</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function QuestionAnswerGroup({
  questionNumber,
  totalQuestions,
  question,
  answer,
  feedback,
  isCurrent,
  isExpanded,
  onToggle,
  allowBars = false,
}) {
  const hasAnswer = Boolean(answer);
  const hasFeedback = Boolean(feedback);
  const bars = feedback?.bars || null;

  return (
    <article
      id={`question-set-${questionNumber}`}
      className={`overflow-hidden rounded-2xl border transition-all duration-300 bg-white ${
        isExpanded ? 'shadow-md border-orange-200' : 'shadow-sm border-gray-100'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-4 text-left"
      >
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[12px] font-black text-white transition-transform ${
            hasAnswer ? 'bg-green-500' : 'bg-orange-500'
          }`}
        >
          {hasAnswer ? <Check className="h-4.5 w-4.5 stroke-[3]" /> : questionNumber}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-bold text-slate-800">
              {isCurrent ? `Question ${questionNumber}` : `Question ${questionNumber} of ${totalQuestions}`}
            </span>
            {hasFeedback && (
              <span className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-1.5 py-0.5 text-[10px] font-bold text-purple-600 border border-purple-100">
                <Sparkles className="h-2.5 w-2.5" />
                Evaluated
              </span>
            )}
          </div>

          {!isExpanded && hasAnswer && (
            <div className="truncate text-[12px] text-gray-400 mt-0.5">{answer.content}</div>
          )}
        </div>

        <ChevronDown
          className={`h-5 w-5 text-gray-300 transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          isExpanded ? 'max-h-[2000px] opacity-100 border-t border-gray-50' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 space-y-3">
          <section className="rounded-xl p-3.5 bg-green-50/50 border-l-4 border-green-500">
            <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-green-600">
              <MessageSquare className="h-3 w-3" />
              Question
            </p>
            <p className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">{question?.content}</p>
          </section>

          {hasAnswer && (
            <section className="rounded-xl p-3.5 bg-gray-50 border-l-4 border-gray-300">
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500">
                <User className="h-3 w-3" />
                Your Answer
              </p>
              <p className="text-[13px] leading-relaxed text-slate-600 whitespace-pre-wrap">{answer?.content}</p>
            </section>
          )}

          {hasFeedback && <QuestionScales bars={bars} locked={!allowBars} />}

          {hasFeedback && (
            <section className="rounded-xl p-3.5 bg-purple-50 border-l-4 border-purple-500 mt-1">
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-purple-600">
                <Sparkles className="h-3 w-3" />
                AI Feedback
              </p>
              <p className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">{feedback?.content}</p>
            </section>
          )}
        </div>
      </div>
    </article>
  );
}
